import styled from 'styled-components';

import { HtmlContent } from '@components/html-content';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { InfoBox } from '@components/info-box';
import { PrefixedPostfixedTextField } from '@components/prefixed-postfixed-text-field';
import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { StepperVertical } from '@components/stepper-vertical';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { Spacing, theme } from '@styles/theme';

import {
  getBaseUrlAffixes,
  getSelectedBaseUrlOption,
  getSpaceBelowAuthInfoItem,
  stripTrailingEmptyParagraphs,
} from './auth-info-layout';

export type AuthInfoText = {
  type: 'text';
  text: string;
};

export type AuthInfoHtml = {
  type: 'html';
  text: { value: string };
};

export type AuthInfoSectionHeading = {
  type: 'section-heading';
  text: string;
};

export type AuthInfoListText = {
  type: 'list-text';
  list: string[];
};

export type AuthInfoListHtml = {
  type: 'list-html';
  list: string[];
};

export type AuthInfoBox = {
  type: 'info-box';
  text: string;
};

export type AuthInfoBoxHtml = {
  type: 'info-box-html';
  text: { value: string };
};

export type AuthInfoInput = {
  type: 'input';
  label: string;
  field: string;
  helpText?: string;
  placeholder?: string;
};

export type AuthInfoBaseUrlOption = {
  label: string;
  value: string;
  isDefault?: boolean;
};

export type AuthInfoBaseUrl = {
  type: 'base-url';
  prefix?: string;
  postfix?: string;
  label: string;
  placeholder?: string;
  helpText?: string;
  options?: AuthInfoBaseUrlOption[];
};

export type AuthInfoItem =
  | AuthInfoText
  | AuthInfoHtml
  | AuthInfoSectionHeading
  | AuthInfoListText
  | AuthInfoListHtml
  | AuthInfoBox
  | AuthInfoBoxHtml
  | AuthInfoInput
  | AuthInfoBaseUrl;

export const BASE_URL_FIELD = 'baseUrl';

/** Help text is authored as html so it can carry a link to the provider's docs. */
const AuthInfoHelpText = ({ helpText }: { helpText?: string }) => {
  if (!helpText) return null;
  return (
    <HtmlContent
      font="regular14"
      color="textHelper"
      html={stripTrailingEmptyParagraphs(helpText)}
    />
  );
};

type AuthInfoItemContentProps = {
  item: AuthInfoItem;
  values?: Record<string, string>;
  onChange?: (field: string, value: string) => void;
};

// Without `values` the fields render uncontrolled, which is what the editor wants.
export const AuthInfoItemContent = ({ item, values, onChange }: AuthInfoItemContentProps) => {
  switch (item.type) {
    case 'text':
      return <Text font="regular14" color="textPrimary" text={item.text} />;
    case 'section-heading':
      return <Text font="semibold14" color="textPrimary" text={item.text} />;
    case 'html':
      return (
        <HtmlContent font="regular14" html={stripTrailingEmptyParagraphs(item.text?.value ?? '')} />
      );
    case 'list-text': {
      const list: string[] = Array.isArray(item.list) ? item.list : [];
      if (list.length === 0) return null;
      return <StepperVertical items={list.map((label) => ({ label }))} />;
    }
    case 'list-html': {
      const list: string[] = Array.isArray(item.list) ? item.list : [];
      if (list.length === 0) return null;
      return (
        <StepperVertical
          items={list.map((html) => ({
            label: <HtmlContent font="regular14" html={stripTrailingEmptyParagraphs(html)} />,
          }))}
        />
      );
    }
    case 'info-box':
      return (
        <InfoBox
          text={item.text ?? ''}
          variant="indigo"
          icon={<IconFormIndicatorInfo color="elementIndigo200" />}
        />
      );
    case 'info-box-html':
      return (
        <InfoBox variant="indigo" icon={<IconFormIndicatorInfo color="elementIndigo200" />}>
          <HtmlContent
            font="regular14"
            color="elementIndigo100"
            html={stripTrailingEmptyParagraphs(item.text?.value ?? '')}
          />
        </InfoBox>
      );
    case 'input': {
      if (!item.field) return null;
      const field = item.field;
      return (
        <Stack space={1}>
          <TextField
            label={item.label}
            placeholder={item.placeholder ?? ''}
            value={values?.[field]}
            onChange={(newValue) => onChange?.(field, newValue)}
          />
          <AuthInfoHelpText helpText={item.helpText} />
        </Stack>
      );
    }
    case 'base-url': {
      if (item.options?.length) {
        return (
          <Stack space={1}>
            <Select
              label={item.label}
              options={item.options.map((option) => ({
                label: option.label,
                value: option.value,
                helpText: option.value,
              }))}
              value={getSelectedBaseUrlOption(values?.[BASE_URL_FIELD], item.options)}
              onChange={(newValue) => onChange?.(BASE_URL_FIELD, newValue)}
              isClearable={false}
              isSearchable={false}
            />
            <AuthInfoHelpText helpText={item.helpText} />
          </Stack>
        );
      }
      const { prefix, postfix } = getBaseUrlAffixes(item);
      let displayValue = values ? (values[BASE_URL_FIELD] ?? '') : undefined;
      if (displayValue) {
        if (prefix && displayValue.startsWith(prefix)) {
          displayValue = displayValue.slice(prefix.length);
        }
        if (postfix && displayValue.endsWith(postfix)) {
          displayValue = displayValue.slice(0, -postfix.length);
        }
      }
      return (
        <Stack space={1}>
          <PrefixedPostfixedTextField
            label={item.label}
            placeholder={item.placeholder ?? ''}
            prefix={prefix}
            postfix={postfix}
            value={displayValue}
            onChange={(newValue) => onChange?.(BASE_URL_FIELD, newValue)}
            hideCopyButton
          />
          <AuthInfoHelpText helpText={item.helpText} />
        </Stack>
      );
    }
    default:
      return null;
  }
};

type AuthInfoItemsProps = {
  items: AuthInfoItem[];
  values?: Record<string, string>;
  onChange?: (field: string, value: string) => void;
};

export const AuthInfoItems = ({ items, values, onChange }: AuthInfoItemsProps) => (
  <>
    {items.map((item, index) => (
      <ItemRow key={index} $spaceBelow={getSpaceBelowAuthInfoItem(item, items[index + 1])}>
        <AuthInfoItemContent item={item} values={values} onChange={onChange} />
      </ItemRow>
    ))}
  </>
);

const ItemRow = styled.div<{ $spaceBelow: Spacing }>(({ $spaceBelow }) => ({
  marginBottom: theme.space[$spaceBelow],
}));
